import { useQuery } from "@tanstack/react-query";
import { getClientConfig } from "../api";
import { useT } from "../i18n";
import { appPlatform } from "./InstallBar";

/**
 * The store buttons (requirement 0142): Apple's and Google's own badges, each
 * shown only while the server holds that store's link (`clients.install_url`,
 * ADR-0209 §5), so both stay hidden until release day. On a phone its own
 * store comes first. /about draws the same buttons on the server.
 */
export default function StoreButtons({ className = "" }: { className?: string }) {
  const t = useT();
  const config = useQuery({
    queryKey: ["client-config"],
    queryFn: getClientConfig,
    staleTime: Infinity,
    retry: false,
  });
  const urls = config.data?.install_urls ?? {};
  const badges = [
    {
      platform: "ios" as const,
      src: "/landing/stores/app-store.svg",
      alt: t.install.appStore,
      className: "h-10",
    },
    // Google's badge carries its own clear space; this height matches Apple's
    // badge inside it.
    {
      platform: "android" as const,
      src: "/landing/stores/google-play.png",
      alt: t.install.googlePlay,
      className: "-my-[9px] h-[58px]",
    },
  ].filter((b) => urls[b.platform]);
  if (badges.length === 0) return null;
  if (appPlatform(navigator.userAgent) === "android") badges.reverse();

  return (
    <div data-testid="store-buttons" className={`flex flex-wrap items-center gap-3 ${className}`}>
      {badges.map((b) => (
        <a key={b.platform} href={urls[b.platform]} rel="noopener" data-store={b.platform}>
          <img src={b.src} alt={b.alt} className={`block w-auto ${b.className}`} />
        </a>
      ))}
    </div>
  );
}
